/* oxlint-disable typescript/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit';
import { IThunkApi } from '../../types/redux';
import { selectFormFields } from '../../selectors';
import { ECurrentErrorField } from 'shared/mf-my-home/types/formFields';
import type { FetchResponse } from '@cian/http-api';
import { fetchPublicSuburbanV1ValidateAddress } from '../../../common/repositories/price-estimator/suburban/v1/validate-address';
import type { TPublicSuburbanV1ValidateAddressResponse } from '../../../common/repositories/price-estimator/suburban/v1/validate-address/types';
import { setError, setHouseId, setFormValid } from '../../slices';

/**
 * Валидатор фильтров для типа оцениваемой недвижимости "Дома"
 * Проверяет наличие обязательных полей, соответствие их условиям
 * Отправляет запрос к АПИ, валидируя адрес
 * */
export const validateHouseFormFilters = createAsyncThunk<void, undefined, IThunkApi>(
  'validateHouseFormFilters',
  async (_, _thunkApi) => {
    const {
      dispatch,
      getState,
      extra: { logger, httpApi },
    } = _thunkApi;
    const state = getState();

    const { address, houseArea, landArea } = selectFormFields(state);

    if (!address) {
      dispatch(
        setError({
          currentErrorField: ECurrentErrorField.address,
          errorText: 'Укажите адрес',
        }),
      );

      dispatch(setFormValid(false));

      return;
    }

    if (!houseArea) {
      dispatch(
        setError({
          currentErrorField: ECurrentErrorField.houseArea,
          errorText: 'Укажите площадь дома',
        }),
      );

      dispatch(setFormValid(false));

      return;
    }

    if (!landArea) {
      dispatch(
        setError({
          currentErrorField: ECurrentErrorField.landArea,
          errorText: 'Укажите площадь участка',
        }),
      );

      dispatch(setFormValid(false));

      return;
    }

    if (houseArea > 9999) {
      dispatch(
        setError({
          currentErrorField: ECurrentErrorField.houseArea,
          errorText: 'Недопустимое значение',
        }),
      );

      dispatch(setFormValid(false));

      return;
    }

    if (landArea > 9999) {
      dispatch(
        setError({
          currentErrorField: ECurrentErrorField.landArea,
          errorText: 'Недопустимое значение',
        }),
      );

      dispatch(setFormValid(false));

      return;
    }

    try {
      const response = (await fetchPublicSuburbanV1ValidateAddress({
        httpApi,
        parameters: { address },
      })) as FetchResponse<TPublicSuburbanV1ValidateAddressResponse>;

      if (response.statusCode === 400) {
        const errors = response.response.errors || [];
        const error: any = errors[0] || {};
        const { key, message } = error;

        if (key && message) {
          dispatch(
            setError({
              currentErrorField: key,
              errorText: message,
            }),
          );
        }

        dispatch(setFormValid(false));

        return;
      }

      if (!response.response.isValid) {
        dispatch(
          setError({
            currentErrorField: ECurrentErrorField.address,
            errorText: 'Оцениваем дома только в Москве и МО',
          }),
        );

        dispatch(setFormValid(false));

        return;
      }

      if (response.response.houseId) {
        dispatch(setHouseId(response.response.houseId));

        dispatch(setFormValid(true));

        return;
      }
    } catch (e) {
      logger.error('Unable to validate suburban house address', e);

      dispatch(setFormValid(false));

      return;
    }

    dispatch(setFormValid(true));

    return;
  },
);
